// The token-gated creative-review screens.
//
// These are the only two reads in the app that belong to nobody. They carry no
// session, they must survive a private-cache purge, and they were the reason
// `isPublicKey` was written — but the namespace was never declared, so both
// keys were hand-typed `['public', …]` literals with nothing checking them.
// A reviewer's page hung on "Loading…" forever once because a purge aborted the
// in-flight query and no session existed to re-drive it. Declared here, the
// purge exemption is `POLICY.public.purgeOnLogout: false` rather than a string
// comparison against a literal somebody has to remember to spell right.

import { api } from '@/api/client';
import { CADENCE, defineQuery } from '@/api/resource';
import type {
  PublicReviewPackage,
} from '@/api/types';
import type {
  MetaPreviewBundle,
  PublicReviewPackageRequest,
} from '@/api/types.gen';

/**
 * The pre-verification resolve response. Hand-declared rather than generated:
 * the legacy handler predates the ts-rs surface and is frozen, so there is
 * nothing upstream to generate it from. It lives here rather than in the route
 * because the resource that fetches it is the lower layer.
 */
export interface PublicReviewResponse {
  creative_name: string;
  previews: MetaPreviewBundle;
  superseded: boolean;
}

/**
 * What the reviewer page asks `/package` with. Only `discriminator` reaches the
 * query key; the share token, the access token and the device id travel in the
 * JSON body and nowhere else, so no credential sits in the cache's keys, in
 * devtools' key list or in anything that logs a key.
 */
export interface PublicReviewPackageInput {
  /**
   * Names the package without being a credential: `s:<shareKey(token)>` for a
   * page opened from `#p=`, or `p:<package id>` for a confirmation emailed
   * before links carried the share token.
   */
  discriminator: string;
  /** The share token from the fragment, so the server refuses a mismatched access. */
  token: string | null;
  /** The stored reviewer access token, the only reviewer credential; null
   *  (nothing stored) is the uniform 404. */
  access: string | null;
  device: string;
}

/**
 * The verified reviewer's package: `POST /public/creative-reviews/package`
 * with the access token in the body (`access`, from localStorage), the share
 * token the page was opened with (`token`), and this browser's device id.
 *
 * The access is not in the key, so the page resets this query itself whenever
 * the stored access changes.
 *
 * `retry: false` comes from the namespace. A reviewer without access gets a
 * 404 by design, and the screen reads that failure as "show the email gate" —
 * retrying it would only delay the gate the reviewer needs.
 *
 * Polls on the snapshot tier only while a preview refresh is in flight, so the
 * fresh frames replace the old ones without a reload, and stops the moment none
 * is.
 */
export const publicReviewPackage = defineQuery({
  namespace: 'public',
  name: 'creativeReview.package',
  volatile: true,
  // Never the token or the access. The input may be the taxonomy test's bare
  // sample string, so it is not dereferenced unless it is an object.
  keyOf: (input: PublicReviewPackageInput) => [
    typeof input === 'object' && input !== null ? input.discriminator : null,
  ],
  cadence: (data: PublicReviewPackage | undefined) =>
    data?.items.some((item) => item.refreshing) ? CADENCE.snapshot : false,
  request: (input: PublicReviewPackageInput, signal) => {
    const body: PublicReviewPackageRequest = {
      token: input.token,
      access: input.access,
      device: input.device,
    };
    return api.post<PublicReviewPackage>('/public/creative-reviews/package', { body, signal });
  },
});

/**
 * The pre-verification review link, resolved from its token.
 *
 * Kept because links minted before verification existed stay valid for their
 * full seven days, and pulling this out from under a reviewer mid-review would
 * be a worse failure than carrying it.
 *
 * The cadence waits on Meta's preview render and stops the moment it lands.
 * `ambient`, not `snapshot`: anonymous reviewers behind one corporate NAT share
 * a single rate-limit bucket, so a roomful of them must not exhaust it.
 */
export const publicReview = defineQuery({
  namespace: 'public',
  name: 'creativeReview.byToken',
  volatile: true,
  keyOf: (token: string) => [token],
  // `retry: 1`, against the namespace default of `false`, restoring what this
  // query inherited from the QueryClient before the registry existed.
  //
  // `POLICY.public.retry = false` was written for the PACKAGE read above, where
  // a 404 is by design and the screen reads the failure as "show the email
  // gate". This read has no such alternate flow: it is an
  // anonymous reviewer following a link, and one transient 503 renders
  // "this review link is invalid, expired, or unavailable" with no retry
  // control, no session, and nothing to re-drive it. Two different intents were
  // flattened into one namespace default; this is the one that needed the
  // override.
  retry: 1,
  refetchOnWindowFocus: true,
  cadence: (data: PublicReviewResponse | undefined) =>
    data && !data.previews.fresh ? CADENCE.snapshot : false,
  request: (token: string, signal) =>
    api.post<PublicReviewResponse>(
      '/public/creative-reviews/resolve',
      { body: { token }, signal },
    ),
});
